import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Adherent } from '../../interfaces/BDD-Ref/adherent';
import { UserSessionService } from '../../services/user.service';
import {
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { MatOption } from '@angular/material/core';
import {
  MatFormField,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ExpandTableService } from '../../services/expand-table.service';
import { GestionComptesService } from '../../services/gestion-comptes.service';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-officine-selector',
  templateUrl: './officine-selector.component.html',
  styleUrl: './officine-selector.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatFormField,
    FormsModule,
    MatLabel,
    MatOption,
    MatIconButton,
    MatIcon,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
})
export class OfficineSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;
  adherents: Adherent[] = [];
  private readonly _destroying$: Subject<void> = new Subject();
  selectedAdherent: Adherent | null = null;
  idAdherent!: number;
  private readonly userService = inject(UserSessionService);
  private isInitialLoad = true;
  @Input() type!: string;

  adherentCtrl = new FormControl();
  filteredAdherents$?: Observable<Adherent[]>;

  constructor(
    private readonly expandTableService: ExpandTableService,
    //private readonly promotionsService: PromotionsService,
    private readonly gestionComptesService: GestionComptesService,
    private readonly userSessionService: UserSessionService,
  ) {}

  ngOnInit(): void {
    const currentUser = localStorage.getItem('currentUserAdmin');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      if (this.userSessionService.getActivite() === 'gpo') {
        combineLatest([this.userSessionService.filiale$])
          .pipe(takeUntil(this._destroying$))
          .subscribe(([filiale]) => {
            this.getOfficineDataGPO(user.email, filiale);
          });
      } else {
        this.getOfficineData(user.email);
      }
    }
    this.filteredAdherents$ = this.adherentCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : (value?.nom ?? ''))),
      map((name) => this.filterAdherents(name)),
    );
  }

  displayFn(adherent: Adherent): string {
    return adherent && adherent.nom ? adherent.nom : '';
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves officine data for a given user and updates the adherent list.
   *
   * @param {string} userEmail - The ID of the user for whom the officine data is to be retrieved.
   * @returns {void}
   */
  getOfficineData(userEmail: string): void {
    this.userService
      .getOfficine(userEmail, this.type)
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: (data) => {
          // Update the list of adherents with the retrieved data
          this.adherents = data;
          if (this.adherents.length > 0) {
            // Check if there are any adherents returned
            if (this.selectedAdherent === null) {
              // If no adherent is selected, check in session storage
              const currentAdherent = sessionStorage.getItem('currentAdherent');
              if (currentAdherent) {
                this.selectedAdherent =
                  this.adherents.find(
                    (adherent) =>
                      adherent.idReferentiel ===
                      JSON.parse(currentAdherent).idReferentiel,
                  ) ?? null;
                this.adherentCtrl.setValue(this.selectedAdherent);
              } else {
                this.selectedAdherent = this.adherents[0];
                this.adherentCtrl.setValue(this.selectedAdherent);
              }
            } else {
              // If an adherent is found, find his index in the list
              const selectedIndex = this.adherents.findIndex(
                (adherent) =>
                  adherent.idReferentiel ===
                  this.selectedAdherent?.idReferentiel,
              );
              // Set the selected value in the form using the found index
              if (selectedIndex !== -1) {
                this.selectedAdherent = this.adherents[selectedIndex];
              }
            }
            if (this.selectedAdherent) {
              this.expandTableService.updateId(
                this.selectedAdherent.idReferentiel,
              );
              this.gestionComptesService.updateId(
                this.selectedAdherent.idAdherent,
              );
              this.userService.setSelectedAdherent(this.selectedAdherent);
            }
          }
        },
      });
  }

  getOfficineDataGPO(userEmail: string, filiale?: string | null): void {
    if (!filiale) {
      this.adherents = [];
      if (this.selectedAdherent) {
        this.onClearAdherent();
      } else {
        this.adherentCtrl.setValue('');
      }
      return;
    }
    this.userService
      .getOfficine(userEmail, this.type)
      .pipe(
        takeUntil(this._destroying$),
        map((data) => data.filter((a: any) => a.filiale === filiale)),
      )
      .subscribe({
        next: (data) => {
          this.adherents = data;

          // si la filiale change ou est supprimée, on vide l’officine
          if (this.selectedAdherent) {
            if (!filiale || this.selectedAdherent.filiale !== filiale) {
              this.onClearAdherent();
            }
          }

          // auto-sélection uniquement au premier chargement
          if (this.isInitialLoad) {
            this.isInitialLoad = false;
            if (!this.selectedAdherent && this.adherents.length > 0) {
              this.selectedAdherent = this.adherents[0];
              this.adherentCtrl.setValue(this.selectedAdherent);
              this.onOfficineSelectionChange(
                this.selectedAdherent.idReferentiel,
              );
            }
          }

          // force le rafraîchissement des options du dropdown
          if (!this.selectedAdherent) {
            this.adherentCtrl.setValue('');
          }
        },
      });
  }

  /**
   * Emit the adherent's referentiel id when the officine is selected
   *
   * @param {number} referentielId - The ID of the adherent's referentiel
   * @returns {void}
   */
  onOfficineSelectionChange(referentielId: number): void {
    this.expandTableService.updateId(referentielId);
    this.gestionComptesService.updateId(this.selectedAdherent?.idAdherent);
    this.userService.setSelectedAdherent(this.selectedAdherent);
    sessionStorage.setItem(
      'currentAdherent',
      JSON.stringify(this.selectedAdherent),
    );
  }

  filterAdherents(value: string): Adherent[] {
    const filterValue = value.toLowerCase();
    return this.adherents.filter((adherent) =>
      adherent.nom?.toLowerCase().includes(filterValue),
    );
  }

  onAdherentSelected(adherent: Adherent) {
    this.selectedAdherent = adherent;
    this.onOfficineSelectionChange(adherent.idReferentiel);
  }

  onClearAdherent() {
    this.adherentCtrl.setValue('');
    this.selectedAdherent = null;
    this.userService.setSelectedAdherent(this.selectedAdherent);
    this.gestionComptesService.updateId(null);
  }

  onInputFocus(): void {
    this.autocompleteTrigger.openPanel();
  }

  onSelectorClosed(): void {
    if (!this.adherentCtrl.value) {
      this.adherentCtrl.setValue(this.selectedAdherent);
    }
  }
}
